import React, { useState, useRef, useCallback, useEffect } from 'react';
import './App.css';
import { processPdf } from './pdfProcessor';
import { splitPdfByPoints, autoSplitPdf } from './pdfSplitter';
import PagePreview from './PagePreview';
import Login from './Login';
import {
  isFileSystemAccessSupported,
  pickBaseFolder,
  saveAllToOrganizedFolder,
} from './fileSystemHelper';

function App() {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem('isLoggedIn') === 'true';
  });

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('username');
    setIsLoggedIn(false);
  };

  const [tab, setTab] = useState('rename');
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  // Split state
  const [splitFile, setSplitFile] = useState(null);
  const [splitPoints, setSplitPoints] = useState(new Set());
  const [splitResults, setSplitResults] = useState([]);

  // Folder save state
  const [baseFolderHandle, setBaseFolderHandle] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [saveStatus, setSaveStatus] = useState(null); // null, 'saving', 'done'

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (files.length > 0 || results.length > 0 || splitResults.length > 0 || splitFile) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [files, results, splitResults, splitFile]);

  const switchTab = (t) => {
    setTab(t);
    setFiles([]);
    setResults([]);
    setSplitFile(null);
    setSplitPoints(new Set());
    setSplitResults([]);
  };

  // --- Rename file handling ---
  const addFiles = useCallback((newFiles) => {
    const pdfFiles = Array.from(newFiles).filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      const unique = pdfFiles.filter(f => !existing.has(f.name + f.size));
      return [...prev, ...unique];
    });
    setResults([]);
  }, []);

  // --- Split file handling ---
  const addSplitFile = useCallback((newFiles) => {
    const pdfFiles = Array.from(newFiles).filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length > 0) {
      setSplitFile(pdfFiles[0]);
      setSplitPoints(new Set());
      setSplitResults([]);
    }
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      if (tab === 'rename') {
        addFiles(e.dataTransfer.files);
      } else {
        addSplitFile(e.dataTransfer.files);
      }
    }
  }, [tab, addFiles, addSplitFile]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      if (tab === 'rename') {
        addFiles(e.target.files);
      } else {
        addSplitFile(e.target.files);
      }
    }
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setFiles([]);
    setResults([]);
    setSplitFile(null);
    setSplitPoints(new Set());
    setSplitResults([]);
  };

  const toggleSplitPoint = (pageNum) => {
    setSplitPoints(prev => {
      const next = new Set(prev);
      if (next.has(pageNum)) {
        next.delete(pageNum);
      } else {
        next.add(pageNum);
      }
      return next;
    });
  };

  // Rename
  const handleRename = async () => {
    setProcessing(true);
    setResults([]);
    try {
      const processed = await Promise.all(files.map(f => processPdf(f)));
      setResults(processed);
    } catch {
      setResults([{ success: false, originalName: '-', message: 'Gagal memproses file.' }]);
    } finally {
      setProcessing(false);
    }
  };

  // Split
  const handleSplit = async () => {
    if (!splitFile || splitPoints.size === 0) return;
    setProcessing(true);
    setSplitResults([]);
    try {
      const parts = await splitPdfByPoints(splitFile, splitPoints);
      setSplitResults(parts);
    } catch {
      setSplitResults([]);
    } finally {
      setProcessing(false);
    }
  };

  // Auto Split
  const handleAutoSplit = async () => {
    if (!splitFile) return;
    setProcessing(true);
    setSplitResults([]);
    try {
      const parts = await autoSplitPdf(splitFile);
      setSplitResults(parts);
    } catch (e) {
      console.error('Auto split error:', e);
      setSplitResults([]);
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadAllRename = () => {
    results.filter(r => r.success).forEach(r => handleDownload(r.blob, r.newFileName));
  };

  const handleDownloadAllSplit = () => {
    splitResults.forEach(p => handleDownload(p.blob, p.fileName));
  };

  // Folder picker
  const handlePickFolder = async () => {
    const handle = await pickBaseFolder();
    if (handle) {
      setBaseFolderHandle(handle);
      setFolderName(handle.name);
    }
  };

  // Save all to organized folder
  const handleSaveAllToFolder = async (files) => {
    if (!baseFolderHandle) {
      alert('Please select a save folder first.');
      return;
    }
    setSaveStatus('saving');
    try {
      const filesToSave = files.map(f => ({
        blob: f.blob,
        fileName: f.fileName || f.newFileName,
        refCode: f.refCode,
      }));
      const results = await saveAllToOrganizedFolder(baseFolderHandle, filesToSave);
      const successCount = results.filter(r => r.success).length;
      alert(`Saved ${successCount}/${results.length} files to ${folderName}`);
      setSaveStatus('done');
    } catch (e) {
      console.error('Save error:', e);
      alert('Error saving files: ' + e.message);
      setSaveStatus(null);
    }
  };

  const handleSaveRenameToFolder = () => {
    const successFiles = results.filter(r => r.success).map(r => ({
      blob: r.blob,
      fileName: r.newFileName,
      refCode: r.newFileName,
    }));
    handleSaveAllToFolder(successFiles);
  };

  const handleSaveSplitToFolder = () => {
    handleSaveAllToFolder(splitResults);
  };

  // Save single file to organized folder
  const handleSaveOneToFolder = async (blob, fileName, refCode) => {
    if (!baseFolderHandle) {
      alert('Please select a save folder first.');
      return;
    }
    try {
      const files = [{ blob, fileName, refCode: refCode || fileName }];
      const results = await saveAllToOrganizedFolder(baseFolderHandle, files);
      if (results[0].success) {
        alert(`Saved to ${folderName}/${results[0].path}`);
      } else {
        alert('Error: ' + results[0].error);
      }
    } catch (e) {
      alert('Error saving file: ' + e.message);
    }
  };

  const successCount = results.filter(r => r.success).length;
  const failCount = results.filter(r => !r.success).length;

  // Show login page if not authenticated
  if (!isLoggedIn) {
    return <Login onLogin={setIsLoggedIn} />;
  }

  return (
    <div className="app-wrapper">
      <button className="logout-btn" onClick={handleLogout}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
        Logout
      </button>
      <div className="app">
        <div className="container">
          <h1 className="title">PDF Tools</h1>

          <div className="tabs">
            <button className={`tab ${tab === 'rename' ? 'tab--active' : ''}`} onClick={() => switchTab('rename')}>
              Rename
            </button>
            <button className={`tab ${tab === 'split' ? 'tab--active' : ''}`} onClick={() => switchTab('split')}>
              Split
            </button>
          </div>

          {/* Folder Picker */}
          {isFileSystemAccessSupported() && (
            <div className="folder-picker">
              <button className="btn btn--folder" onClick={handlePickFolder}>
                📁 {folderName ? `Folder: ${folderName}` : 'Set Save Folder'}
              </button>
              {folderName && (
                <span className="folder-hint">Files will auto-organize to subfolders by code/month</span>
              )}
            </div>
          )}

          <p className="subtitle">
            {tab === 'rename'
              ? 'Rename PDF files based on Branch reference code'
              : 'Select a PDF, then click between pages to split'}
          </p>

          {/* Dropzone — show when rename has no results, or split has no file */}
          {((tab === 'rename') || (tab === 'split' && !splitFile)) && (
            <div
              className={`dropzone ${dragActive ? 'dropzone--active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".pdf"
                multiple={tab === 'rename'}
                onChange={handleFileChange}
                hidden
              />
              <div className="dropzone-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <p className="dropzone-text">
                {tab === 'rename'
                  ? <>Drag & drop PDF files here, or <span className="dropzone-link">browse</span></>
                  : <>Drag & drop a PDF here, or <span className="dropzone-link">browse</span></>}
              </p>
            </div>
          )}

          {/* Rename: file list */}
          {tab === 'rename' && files.length > 0 && (
            <div className="file-list">
              <div className="file-list-header">
                <span className="file-count">{files.length} file{files.length > 1 ? 's' : ''} selected</span>
                <button className="btn btn--text" onClick={clearAll}>Clear all</button>
              </div>
              <ul className="file-items">
                {files.map((file, index) => (
                  <li key={file.name + file.size} className="file-item">
                    <svg className="file-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span className="file-item-name">{file.name}</span>
                    <span className="file-item-size">{(file.size / 1024).toFixed(0)} KB</span>
                    <button className="file-item-remove" onClick={(e) => { e.stopPropagation(); removeFile(index); }} title="Remove">
                      &times;
                    </button>
                  </li>
                ))}
              </ul>
              <button
                className="btn btn--primary"
                onClick={handleRename}
                disabled={processing}
              >
                {processing ? 'Processing...' : `Rename ${files.length > 1 ? 'All ' : ''}PDF${files.length > 1 ? 's' : ''}`}
              </button>
            </div>
          )}

          {/* Split: page preview */}
          {tab === 'split' && splitFile && splitResults.length === 0 && (
            <div className="split-preview-section">
              <div className="split-file-header">
                <span className="file-count">{splitFile.name}</span>
                <button className="btn btn--text" onClick={clearAll}>Change file</button>
              </div>
              <PagePreview
                file={splitFile}
                splitPoints={splitPoints}
                onToggleSplit={toggleSplitPoint}
              />
              <div className="split-actions">
                {splitPoints.size > 0 && (
                  <span className="split-summary">
                    {splitPoints.size} split point{splitPoints.size > 1 ? 's' : ''} = {splitPoints.size + 1} files
                  </span>
                )}
                <div className="split-buttons">
                  <button
                    className="btn btn--secondary split-btn"
                    onClick={handleAutoSplit}
                    disabled={processing}
                    title="Automatically detect split points based on page patterns and reference codes"
                  >
                    {processing ? 'Processing...' : '✨ Auto Split'}
                  </button>
                  <button
                    className="btn btn--primary split-btn"
                    onClick={handleSplit}
                    disabled={processing || splitPoints.size === 0}
                  >
                    {processing ? 'Splitting...' : `Split into ${splitPoints.size + 1} files`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Rename Results */}
          {tab === 'rename' && results.length > 0 && (
            <div className="results">
              <div className="results-header">
                <h2 className="results-title">
                  Results
                  <span className="results-counts">
                    <span className="badge badge--success">{successCount} OK</span>
                    {failCount > 0 && <span className="badge badge--error">{failCount} Failed</span>}
                  </span>
                </h2>
                <div className="results-actions">
                  {successCount > 1 && (
                    <button className="btn btn--outline" onClick={handleDownloadAllRename}>
                      Download All ({successCount})
                    </button>
                  )}
                  {baseFolderHandle && successCount > 0 && (
                    <button
                      className="btn btn--save"
                      onClick={handleSaveRenameToFolder}
                      disabled={saveStatus === 'saving'}
                    >
                      {saveStatus === 'saving' ? 'Saving...' : `💾 Save All to ${folderName}`}
                    </button>
                  )}
                </div>
              </div>
              <div className="results-table-wrap">
                <table className="results-table">
                  <thead>
                    <tr>
                      <th>Original</th>
                      <th>New Name</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result, index) => (
                      <tr key={index}>
                        <td className="cell-name">{result.originalName}</td>
                        <td className="cell-name">{result.success ? result.newFileName : '-'}</td>
                        <td>
                          <span className={`badge ${result.success ? 'badge--success' : 'badge--error'}`}>
                            {result.success ? 'OK' : 'Failed'}
                          </span>
                        </td>
                        <td>
                          {result.success ? (
                            <div className="action-buttons">
                              <button className="btn btn--small" onClick={() => handleDownload(result.blob, result.newFileName)}>
                                Download
                              </button>
                              {baseFolderHandle && (
                                <button
                                  className="btn btn--small btn--save-small"
                                  onClick={() => handleSaveOneToFolder(result.blob, result.newFileName, result.newFileName)}
                                  title={`Save to ${folderName}`}
                                >
                                  💾
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="error-msg">{result.message}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Split Results */}
          {tab === 'split' && splitResults.length > 0 && (
            <div className="results">
              <div className="results-header">
                <h2 className="results-title">Split Results</h2>
                <div className="results-actions">
                  {splitResults.length > 1 && (
                    <button className="btn btn--outline" onClick={handleDownloadAllSplit}>
                      Download All ({splitResults.length})
                    </button>
                  )}
                  {baseFolderHandle && splitResults.length > 0 && (
                    <button
                      className="btn btn--save"
                      onClick={handleSaveSplitToFolder}
                      disabled={saveStatus === 'saving'}
                    >
                      {saveStatus === 'saving' ? 'Saving...' : `💾 Save All to ${folderName}`}
                    </button>
                  )}
                </div>
              </div>
              <div className="results-table-wrap">
                <table className="results-table">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Pages</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {splitResults.map((part, index) => (
                      <tr key={index}>
                        <td className="cell-name">{part.fileName}</td>
                        <td>{part.pages}</td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn btn--small" onClick={() => handleDownload(part.blob, part.fileName)}>
                              Download
                            </button>
                            {baseFolderHandle && (
                              <button
                                className="btn btn--small btn--save-small"
                                onClick={() => handleSaveOneToFolder(part.blob, part.fileName, part.refCode)}
                                title={`Save to ${folderName}`}
                              >
                                💾
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button className="btn btn--text split-back-btn" onClick={clearAll}>
                Split another PDF
              </button>
            </div>
          )}
        </div>
        <footer className="footer">
          {/* <p>build with ❤️ from your husband</p> */}
        </footer>
      </div>
    </div>
  );
}

export default App;
